/**
 * AdminFinancials.tsx
 * 
 * Admin page for comprehensive financial management and analytics.
 * Displays revenue metrics, payment analytics, subscription data,
 * and financial reports for business intelligence.
 * 
 * Refactored to use reusable components and custom hooks for better maintainability.
 */

import React from 'react'
import { 
  Download,
  BarChart3
} from 'lucide-react'
import { useAdminFinancials } from '../hooks/useAdminFinancials'
import AdminFinancialMetrics from '../components/Admin/AdminFinancialMetrics'
import AdminRevenueBreakdown from '../components/Admin/AdminRevenueBreakdown'
import AdminRecentTransactions from '../components/Admin/AdminRecentTransactions'
import AdminTopEarners from '../components/Admin/AdminTopEarners'

const AdminFinancials: React.FC = () => {
  const {
    financialData,
    loading,
    selectedPeriod,
    setSelectedPeriod,
    selectedView,
    setSelectedView,
    error
  } = useAdminFinancials()

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">{error}</p>
      </div>
    )
  }

  if (!financialData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Failed to load financial data</p>
      </div>
    )
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Financial Overview</h1>
          <p className="text-gray-600">Monitor revenue, subscriptions, and financial performance</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          <button className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors">
            <Download className="h-4 w-4 mr-2 inline" />
            Export Report
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <AdminFinancialMetrics
        totalRevenue={financialData.totalRevenue}
        monthlyRevenue={financialData.monthlyRevenue}
        pendingPayouts={financialData.pendingPayouts}
        commissionEarned={financialData.commissionEarned}
        revenueGrowth={financialData.revenueGrowth}
      />

      {/* Revenue Breakdown and Recent Transactions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AdminRevenueBreakdown
          revenueBreakdown={financialData.revenueBreakdown}
          totalRevenue={financialData.totalRevenue}
        />
        <AdminRecentTransactions
          transactions={financialData.recentTransactions}
        />
      </div>

      {/* Top Earners */}
      <AdminTopEarners topEarners={financialData.topEarners} />

      {/* Revenue Chart */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Revenue Trend</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setSelectedView('overview')}
              className={`px-3 py-1 text-sm rounded-md ${
                selectedView === 'overview' 
                  ? 'bg-primary-500 text-white' 
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setSelectedView('detailed')}
              className={`px-3 py-1 text-sm rounded-md ${
                selectedView === 'detailed' 
                  ? 'bg-primary-500 text-white' 
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              Detailed
            </button>
          </div>
        </div>
        
        <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
          <div className="text-center">
            <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">Revenue chart visualization</p>
            <p className="text-sm text-gray-400">Chart component would be implemented here</p>
          </div>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-600 mb-2">Average Revenue Per User</h4>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(financialData.averageRevenuePerUser)}
            </p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-600 mb-2">Conversion Rate</h4>
            <p className="text-2xl font-bold text-gray-900">12.5%</p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-600 mb-2">Customer Lifetime Value</h4>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(245.67)}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminFinancials 